// Board.js
import { useEffect, useMemo, useState } from "react";
import "./Board.css";
import Loading from "../components/Loading";
import { apiFetch } from "../components/api";

function formatDateTime(v) {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
}

function pick(obj, keys, fallback = "") {
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
}

export default function Board() {
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState("");

  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);

  const [search, setSearch] = useState("");

  const [composeOpen, setComposeOpen] = useState(false);
  const [busyCreate, setBusyCreate] = useState(false);
  const [busyComment, setBusyComment] = useState(false);

  const [newPost, setNewPost] = useState({
    title: "",
    content: "",
    is_notice: false,
    project: "",
    product: "",
  });
  const [newPostImages, setNewPostImages] = useState([]);
  const [newComment, setNewComment] = useState("");

  const username = localStorage.getItem("username") || "";

  const postImages = Array.isArray(selectedPost?.images) ? selectedPost.images : [];

  const canDeletePost = useMemo(() => {
    if (!selectedPost) return false;
    const author = pick(selectedPost, ["author_name", "author", "username"], "");
    return username === "admin" || (author && username === author);
  }, [selectedPost, username]);

  const selectedComments = useMemo(() => {
    const a = selectedPost?.comments;
    const b = selectedPost?.comment_set;
    const c = selectedPost?.comment_list;
    if (Array.isArray(a)) return a;
    if (Array.isArray(b)) return b;
    if (Array.isArray(c)) return c;
    return [];
  }, [selectedPost]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams();
      if ((search || "").trim()) params.append("search", search.trim());

      const qs = params.toString();
      const data = await apiFetch(`/api/board/posts/${qs ? `?${qs}` : ""}`, {
        cache: "no-store",
      });

      const list = data && data.results ? data.results : data || [];

      const sorted = (list || []).slice().sort((a, b) => {
        const an = a?.is_notice ? 1 : 0;
        const bn = b?.is_notice ? 1 : 0;
        if (an !== bn) return bn - an;
        const at = new Date(a?.created_at || 0).getTime();
        const bt = new Date(b?.created_at || 0).getTime();
        return bt - at;
      });

      setPosts(sorted);

      if (!selectedPost && sorted.length > 0) {
        await fetchPostDetail(sorted[0].id);
      }
    } catch (e) {
      setError(`게시글 목록 로드 에러 : ${e?.message || "알 수 없는 오류"}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchPostDetail = async (id) => {
    if (!id) return;
    try {
      setLoadingDetail(true);
      setError("");

      const data = await apiFetch(`/api/board/posts/${id}/`, {
        cache: "no-store",
      });

      setSelectedPost(data);
    } catch (e) {
      setError(`게시글 로드 에러 : ${e?.message || "알 수 없는 오류"}`);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSelectPost = (postId) => {
    fetchPostDetail(postId);
  };

  const handleSearch = () => {
    fetchPosts();
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (busyCreate) return;

    const title = (newPost.title || "").trim();
    const content = (newPost.content || "").trim();
    if (!title || !content) {
      alert("제목과 내용을 입력해 주세요.");
      return;
    }

    try {
      setBusyCreate(true);

      const fd = new FormData();
      fd.append("title", title);
      fd.append("content", content);
      fd.append("is_notice", String(!!newPost.is_notice));
      if ((newPost.project || "").trim()) fd.append("project", newPost.project.trim());
      if ((newPost.product || "").trim()) fd.append("product", newPost.product.trim());
      fd.append("username", username);
      if (newPostImages.length) {
        newPostImages.forEach((f) => fd.append("images", f));
      }

      await apiFetch("/api/board/posts/", {
        method: "POST",
        body: fd,
      });

      setNewPost({
        title: "",
        content: "",
        is_notice: false,
        project: "",
        product: "",
      });
      setNewPostImages([]);
      setComposeOpen(false);

      await fetchPosts();
    } catch (e2) {
      alert(`게시글 등록 에러 : ${e2?.message || "알 수 없는 오류"}`);
    } finally {
      setBusyCreate(false);
    }
  };

  const handleDeletePost = async () => {
    if (!selectedPost) return;
    if (!window.confirm("이 게시글을 삭제하시겠습니까?")) return;

    try {
      await apiFetch(`/api/board/posts/${selectedPost.id}/`, {
        method: "DELETE",
        body: { username },
      });

      setSelectedPost(null);
      await fetchPosts();
    } catch (e) {
      alert(`게시글 삭제 에러 : ${e?.message || "알 수 없는 오류"}`);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!selectedPost) return;
    if (busyComment) return;

    const text = (newComment || "").trim();
    if (!text) {
      alert("댓글 내용을 입력해 주세요.");
      return;
    }

    try {
      setBusyComment(true);

      const payload = {
        post: selectedPost.id,
        content: text,
        username,
      };

      await apiFetch("/api/board/comments/", {
        method: "POST",
        body: payload,
      });

      setNewComment("");
      await fetchPostDetail(selectedPost.id);
    } catch (e3) {
      alert(`댓글 등록 에러 : ${e3?.message || "알 수 없는 오류"}`);
    } finally {
      setBusyComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("댓글을 삭제하시겠습니까?")) return;

    try {
      await apiFetch(`/api/board/comments/${commentId}/`, {
        method: "DELETE",
        body: { username },
      });

      if (selectedPost) await fetchPostDetail(selectedPost.id);
    } catch (e) {
      alert(`댓글 삭제 에러 : ${e?.message || "알 수 없는 오류"}`);
    }
  };

  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="board-page">
      {loading ? (
        <Loading active={loading} />
      ) : (
        <>
          <div className={`board-left ${composeOpen ? "compose-open" : "compose-closed"}`}>
            <div className="board-card board-list-card">
              <div className="board-header">
                <h2>게시판</h2>

                <div className="board-search">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="제목/내용 검색"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSearch();
                    }}
                  />
                  <button type="button" onClick={handleSearch}>
                    검색
                  </button>
                </div>
              </div>

              {error ? <div className="board-error">{error}</div> : null}

              <div className="board-table-wrap">
                <table className="board-table">
                  <thead>
                    <tr>
                      <th>공지</th>
                      <th>제목</th>
                      <th>프로젝트</th>
                      <th>제품</th>
                      <th>작성자</th>
                      <th>조회</th>
                      <th>작성일</th>
                    </tr>
                  </thead>

                  <tbody>
                    {(posts || []).length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ textAlign: "center", padding: 16, color: "#6b7280" }}>
                          게시글이 없습니다.
                        </td>
                      </tr>
                    ) : (
                      (posts || []).map((p) => {
                        const selected = selectedPost?.id === p.id;

                        const title = pick(p, ["title"], "(제목 없음)");
                        const project = pick(p, ["project_name", "project"], "-");
                        const product = pick(p, ["product_name", "product"], "-");
                        const author = pick(p, ["author_name", "author", "username"], "-");
                        const views = pick(p, ["view_count", "views"], 0);
                        const createdAt = pick(p, ["created_at"], "");

                        return (
                          <tr
                            key={p.id}
                            className={selected ? "selected" : ""}
                            onClick={() => handleSelectPost(p.id)}
                            style={{ cursor: "pointer" }}
                          >
                            <td>{p.is_notice ? "공지" : ""}</td>
                            <td title={title}>
                              {title}
                              {!!p.comment_count && (
                                <span className="board-comment-count">[{p.comment_count}]</span>
                              )}
                            </td>
                            <td title={project}>{project}</td>
                            <td title={product}>{product}</td>
                            <td title={author}>{author}</td>
                            <td>{Number(views || 0)}</td>
                            <td>{formatDateTime(createdAt)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            
            <details
              className="board-card board-compose-card"
              open={composeOpen}
              onToggle={(e) => setComposeOpen(e.target.open)}
            >
              <summary className="board-compose-summary">새 글 작성</summary>

              <div className="board-compose-body">
                <form onSubmit={handleCreatePost}>
                  <div className="form-row">
                    <label>제목</label>
                    <input
                      value={newPost.title}
                      onChange={(e) => setNewPost((prev) => ({ ...prev, title: e.target.value }))}
                    />
                  </div>

                  <div className="form-row">
                    <label>프로젝트</label>
                    <input
                      value={newPost.project}
                      onChange={(e) => setNewPost((prev) => ({ ...prev, project: e.target.value }))}
                    />
                  </div>

                  <div className="form-row">
                    <label>제품</label>
                    <input
                      value={newPost.product}
                      onChange={(e) => setNewPost((prev) => ({ ...prev, product: e.target.value }))}
                    />
                  </div>

                  <div className="form-row">
                    <label>사진</label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => setNewPostImages(Array.from(e.target.files || []))}
                    />
                  </div>

                  <div className="form-row">
                    <label>내용</label>
                    <textarea
                      value={newPost.content}
                      onChange={(e) => setNewPost((prev) => ({ ...prev, content: e.target.value }))}
                    />
                  </div>

                  <div className="form-row-inline">
                    <label>
                      <input
                        type="checkbox"
                        checked={!!newPost.is_notice}
                        onChange={(e) => setNewPost((prev) => ({ ...prev, is_notice: e.target.checked }))}
                      />
                      공지글
                    </label>
                  </div>

                  <div className="form-actions">
                    <button type="submit" disabled={busyCreate}>
                      등록
                    </button>
                  </div>
                </form>
              </div>
            </details>
          </div>

          <div className="board-right board-card">
            <div className="board-detail">
              {loadingDetail ? (
                <div className="board-loading">게시글을 불러오는 중입니다.</div>
              ) : !selectedPost ? (
                <div className="board-empty">왼쪽에서 게시글을 선택하세요.</div>
              ) : (
                <>
                  <div className="board-detail-top">
                    <div className="board-detail-header">
                      <h3>{pick(selectedPost, ["title"], "(제목 없음)")}</h3>
                      {selectedPost.is_notice ? <span className="badge-notice">공지</span> : null}

                      {canDeletePost ? (
                        <button type="button" className="btn-delete-post" onClick={handleDeletePost}>
                          글 삭제
                        </button>
                      ) : null}
                    </div>

                    <div className="board-detail-meta">
                      <span>작성자: {pick(selectedPost, ["author_name", "author", "username"], "-")}</span>
                      <span>조회수: {Number(pick(selectedPost, ["view_count", "views"], 0) || 0)}</span>
                      <span>작성일: {formatDateTime(pick(selectedPost, ["created_at"], ""))}</span>
                    </div>

                    {(pick(selectedPost, ["project_name", "project"], "") || pick(selectedPost, ["product_name", "product"], "")) ? (
                      <div className="board-detail-project">
                        {pick(selectedPost, ["project_name", "project"], "") ? (
                          <span>프로젝트: {pick(selectedPost, ["project_name", "project"], "")}</span>
                        ) : null}
                        {pick(selectedPost, ["product_name", "product"], "") ? (
                          <span>제품: {pick(selectedPost, ["product_name", "product"], "")}</span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>

                  <div className="board-detail-content">
                    {postImages.length ? (
                      <div style={{ marginBottom: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {postImages
                          .slice()
                          .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
                          .map((img) => (
                            <img
                              key={img.id}
                              src={img.image_url}
                              alt="첨부 이미지"
                              className="board-detail-image"
                            />
                          ))}
                      </div>
                    ) : null}
                    {pick(selectedPost, ["content"], "")}
                  </div>

                  <div className="board-comments">
                    <h4>댓글</h4>

                    <div className="board-comment-list">
                      {selectedComments.length === 0 ? (
                        <div className="board-empty">등록된 댓글이 없습니다.</div>
                      ) : (
                        selectedComments.map((c) => {
                          const cAuthor = pick(c, ["author_name", "author", "username"], "-");
                          const canDeleteComment =
                            username === "admin" || (cAuthor && username === cAuthor);

                          return (
                            <div key={c.id} className="board-comment-item">
                              <div className="board-comment-meta">
                                <span className="author">{cAuthor}</span>
                                <span className="time">{formatDateTime(pick(c, ["created_at"], ""))}</span>

                                {canDeleteComment ? (
                                  <button
                                    type="button"
                                    className="btn-delete-comment"
                                    onClick={() => handleDeleteComment(c.id)}
                                  >
                                    삭제
                                  </button>
                                ) : null}
                              </div>
                              <div className="board-comment-content">{pick(c, ["content"], "")}</div>
                            </div>
                          );
                        })
                      )}
                    </div>

                    <form onSubmit={handleAddComment} className="board-comment-compose">
                      <textarea
                        placeholder="댓글을 입력하세요."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        disabled={busyComment}
                      />
                      <button type="submit" className="btn-comment-submit" disabled={busyComment || !newComment.trim()}>
                        댓글 등록
                      </button>
                    </form>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
